import { API_CONFIG, buildApiUrl } from '../config/api';
import { Plan, PlansResponse } from '../models/Plan';
import { secureTokenManager } from '../utils/secureTokenManager';

export interface CreatePlanRequest {
  planName: string;
  amount: number;
  currency: string;
  features: string[];
  validity: number;
  description?: string;
}

export interface CreatePlanResponse {
  success: boolean;
  message: string;
  data: {
    plan: Plan;
  };
}

export interface UpdatePlanRequest {
  planName?: string;
  amount?: number;
  validity?: number;
  isActive?: boolean;
  description?: string;
  currency?: string;
  features?: string[];
}

export interface UpdatePlanResponse {
  success: boolean;
  message: string;
  data: {
    plan: Plan;
  };
}

export class PlanService {
  private static baseURL = API_CONFIG.baseURL;

  /**
   * Fetch all plans from the API
   * @param activeOnly - Set to true to return only active plans
   */
  static async getPlans(activeOnly?: boolean): Promise<PlansResponse> {
    let url = buildApiUrl('/plans');
    
    // Add query parameter if activeOnly is specified
    if (activeOnly === true) {
      url += '?activeOnly=true';
    }
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          // Cache-busting headers to prevent browser caching
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: PlansResponse = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get a specific plan by ID
   */
  static async getPlanById(planId: string): Promise<Plan> {
    const url = buildApiUrl('/plans');
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: PlansResponse = await response.json();
      const plan = data.data.plans.find((p: Plan) => p.planId === planId);
      
      if (!plan) {
        throw new Error('Plan not found');
      }

      return plan;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a new plan
   */
  static async createPlan(planData: CreatePlanRequest): Promise<CreatePlanResponse> {
    const url = buildApiUrl('/plans');
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${secureTokenManager.getAdminSessionToken()}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        },
        body: JSON.stringify(planData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: CreatePlanResponse = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update a plan
   * @param planId - The ID of the plan to update
   * @param updateData - The data to update (amount and/or isActive)
   */
  static async updatePlan(planId: string, updateData: UpdatePlanRequest): Promise<UpdatePlanResponse> {
    const url = buildApiUrl(`/plans/${planId}`);
    
    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${secureTokenManager.getAdminSessionToken()}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: UpdatePlanResponse = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }
}
